import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ShelfLocations } from '../../components/warehouse/ShelfLocations';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  FolderTree,
  Plus,
  Save,
  Search,
  Sparkles,
  Upload,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button, buttonVariants } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input, inputClass, checkboxClass } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { usePermission } from '../../auth/usePermission';
import { PermissionName } from '../../auth/permissions';
import { cn } from '../../utils/cn';
import { ArticleImage } from '../../components/ui/ArticleImage';
import { CategoryPicker } from '../../components/CategoryPicker';
import { ProductCoefficientInheritance } from '../../components/ProductCoefficientInheritance';
import { ImportDialog, type ImportPreviewRow } from '../../components/ImportDialog';
import { formatEUR } from '../../lib/format';

const ORIGINS = ['OE', 'OEM', 'OES', 'AFTERMARKET', 'EXCHANGE'] as const;
const PACKAGING_UNITS = ['PIECE', 'SET', 'KIT', 'PAIR', 'LITER', 'BOX'] as const;

interface Spec {
  label: string;
  value: string;
  unit: string | null;
}

interface Product {
  id: string;
  sku: string;
  name: string;
  shortName: string | null;
  brand: string | null;
  categoryId: string | null;
  categoryName: string | null;
  /** False when the category (or an ancestor) is deactivated — the product is out of every search. */
  categoryVisible: boolean;
  ean: string | null;
  /** Second barcode (migration 144). */
  ean2: string | null;
  manufacturerRef: string | null;
  origin: string | null;
  purchasePriceHT: number;
  coefficient: number | null;
  sellingPriceOverride: number | null;
  vatRate: number | null;
  lowStockThreshold: number | null;
  packagingUnit: string | null;
  weightKg: number | null;
  description: string | null;
  // TecDoc-enriched, read-only.
  oemReferences: string[];
  crossReferences: string[];
  imageUrls: string[];
  specifications: Spec[];
  availableStock: number;
  active: boolean;
  version: number;
  createdAt: string;
  // ---- extended "Add Product" sheet fields ----
  maxStockThreshold: number | null;
  /** Shelf locations, main first — recorded by the Shelving scan, read-only here (migration 144). */
  locations: string[];
  mainSupplierId: string | null;
  subGroupCode: string | null;
  productGroupId: string | null;
  attributeId: string | null;
  fileId: string | null;
  referenceType1: string | null;
  reference2: string | null;
  referenceType2: string | null;
  reference3: string | null;
  referenceType3: string | null;
  reference4: string | null;
  referenceType4: string | null;
  productKeys: string | null;
  heightMm: number | null;
  lengthMm: number | null;
  widthMm: number | null;
  inch: number | null;
  packingMaterial: string | null;
  labelType: string | null;
  photoUrl: string | null;
  popularityCode: string | null;
  statusCode: string | null;
  returnCode: string | null;
  currency1: string | null;
  currency2: string | null;
  groupCodeDiscount2: string | null;
  groupCodeIn1: string | null;
  groupCodeIn2: string | null;
  groupCodeEx1: string | null;
  groupCodeEx2: string | null;
  minOrderQty: number | null;
  conditioningQty: number | null;
  disposalFee: number | null;
  depositAmountHt: number | null;
  environmentalSurcharge: number | null;
  grossWeightKg: number | null;
}

/** Candidate TecDoc article returned by /admin/tecdoc/article-search. */
interface ArticleMatch {
  artId: number;
  articleNr: string;
  brand: string;
  matchedType: string;
}

/** The full TecDoc article behind a match — what enrichment copies onto a product. */
interface ArticleSnapshot {
  artId: number;
  articleNr: string;
  brand: string;
  designation: string | null;
  oemReferences: string[];
  crossReferences: string[];
  eans: string[];
  imageUrls: string[];
  specifications: Spec[];
  ktypes: number[];
}

/** /admin/tecdoc/article/:artId — the snapshot plus the category it auto-classifies to. */
interface ArticlePreview {
  snapshot: ArticleSnapshot;
  category: { id: string; name: string } | null;
}

/** A pre-save duplicate hit from /products/validate (an existing product sharing an identifier). */
interface DuplicateHit {
  field: 'sku' | 'ean' | 'manufacturerRef';
  id: string;
  sku: string;
  name: string;
}

interface ListResponse {
  data: Product[];
  total: number;
  page: number;
  pageSize: number;
  /** Parts a supplier can deliver that we don't carry. Present only when our own list came back
   *  empty for a reference-shaped search. No product id — these are quotes, not records. */
  supplierCandidates?: SupplierCandidate[];
}

interface SupplierCandidate {
  id: string;
  sku: string;
  name: string;
  brand: string | null;
  manufacturerRef: string | null;
  supplier?: {
    supplierCode?: string;
    supplierName?: string;
    supplierArticleNo: string;
    availableQty: number | null;
    leadTimeDays: number | null;
    unitPriceHT?: number;
    indicativeSellPriceHT: number | null;
  };
}

interface CatalogImportResult {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: { row: number; ref: string; message: string }[];
  warnings: { row: number; ref: string; message: string }[];
}

/** Map a catalog preview row status to a Badge variant. */
const catalogStatusVariant = (s: string) =>
  s === 'NEW' || s === 'UPDATE'
    ? 'success'
    : s === 'ERROR'
      ? 'destructive'
      : s === 'DUPLICATE' || s === 'NOT_FOUND'
        ? 'warning'
        : 'muted';

type SortKey =
  | 'sku'
  | 'name'
  | 'brand'
  | 'purchasePriceHT'
  | 'availableStock'
  | 'active'
  | 'createdAt';

const PAGE_SIZE = 25;
const euro = (n: number | null): string => (n == null ? '—' : formatEUR(n));

/**
 * Worked example offered next to the blank import template, in the standardized column order:
 *   No,Brand,Category name,Category code,Category id,Supplier,Coefficient,Net Price EXW,Stock
 *
 * It is filled the way a hand-prepared file SHOULD be filled, because the two lanes are not
 * equally reliable and a header-only template cannot say so:
 *
 *  - `Category code` is the lane that works. It matches `product_categories.code`, which is
 *    UNIQUE, so it can never be ambiguous. TecDoc-mirrored codes read `STR-<node id>` (a tree
 *    node) or `GA-<generic article>-<node>` (a leaf part type); a category created here reads
 *    `CUST-<uuid>`. Rows 2 and 4 sit on sub-categories to show the deeper codes are written
 *    exactly the same way — depth changes nothing about the format.
 *  - `Category name` is a fallback, and a weak one: 2,203 of the 5,105 distinct names in the
 *    tree are carried by more than one category (there are five "Filtre à huile"), and an
 *    ambiguous name resolves to nothing. Row 6 uses a name with no code deliberately — that
 *    name is unique, so it resolves — while every other row names the category for the human
 *    reading the file and lets the code do the matching.
 *  - `Supplier` is left empty for the same reason as `Category id`, and for a sharper one: a code
 *    that resolves to nothing is a hard row error ("Unknown supplier code"), not a blank field.
 *    Supplier codes differ per installation, so any code baked into a shipped example is a row
 *    that fails on somebody's machine — this example used to name SUP_A/SUP_B/SUP_D, placeholders
 *    that no longer exist anywhere (migration 099). Use the "apply to every row" default beside
 *    the upload, or add your own codes from /admin/suppliers.
 *  - `Category id` is left empty on purpose. It takes our internal UUID, which differs between
 *    installations and is not something anyone should be typing by hand.
 *  - `Stock` is left empty because the catalog import never writes stock (it is reported as an
 *    ignored column); stock moves only through Purchasing & Stock Management's ledger.
 */
const PRODUCT_IMPORT_EXAMPLE = [
  'W 712/75,MANN-FILTER,Filtre à huile,GA-100259-7,,,1.45,3.20,',
  'HU 718/5 X,MANN-FILTER,Filtre à huile (sous-catégorie),GA-103779-7,,,1.45,6.85,',
  'C 30 005,MANN-FILTER,Filtre à air,STR-100260,,,1.40,8.10,',
  '09.A821.11,BREMBO,Disque de frein (sous-catégorie),GA-100032-82,,,1.55,32.40,',
  'E11,VARTA,Batterie,STR-100042,,,1.35,78.00,',
  '5.0577.10,MONROE,Pompe à eau additionnelle,,,,1.50,41.90,',
  '90R-02075,VALEO,Amortisseur,GA-100121-854,,,1.50,54.30,',
  'GDB1330,DELPHI,Disque de frein,STR-100032,,,1.60,28.75,',
];

/** Screen — Product management (catalogue CRUD). OWNER / MANAGER / ADMIN (CATALOG_WRITE). */
export default function ProductManagement() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  // Full catalogue editors create/import; a cost-only PURCHASER only edits the price of existing rows.
  const canEditCatalog = usePermission(PermissionName.CATALOG_WRITE);
  /**
   * Correcting stock from this list is the same act as a zone count, so it takes the same
   * authority — STOCK_RECEIVE (OWNER / MANAGER / WAREHOUSE_KEEPER / POLYVALENT), which the C2
   * catalogue names as covering inventory adjustment — and not the CATALOG_WRITE that opens the
   * rest of this screen. An ADMIN or PURCHASER edits every other column here and still cannot
   * move stock, exactly as on the warehouse screens.
   */
  const canAdjustStock = usePermission(PermissionName.STOCK_RECEIVE);
  /** The row whose Stock cell is open for editing, with its in-progress text. */
  const [stockEdit, setStockEdit] = useState<{ id: string; value: string } | null>(null);
  /** Row id whose adjustment is in flight — the cell stays read-only until it lands. */
  const [stockSaving, setStockSaving] = useState<string | null>(null);
  // Escape has to cancel, but it can only leave the field by blurring it — and blur is what
  // commits. A ref, not state: the flag is read inside the very blur it has to suppress.
  const stockCancelled = useRef(false);
  const [rows, setRows] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [supplierCandidates, setSupplierCandidates] = useState<SupplierCandidate[]>([]);
  // "Create product" on a supplier candidate: opens the normal create drawer, seeded from the
  // quote. Everything stays editable — the operator still owns cost, coefficient and category.
  const [creatingFrom, setCreatingFrom] = useState<SupplierCandidate | null>(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  // Seed from the global navbar search (?q=…) so landing here from the nav runs the filter.
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get('q') ?? '');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [categoryId, setCategoryId] = useState('');
  /** Worklist filter for the fields the bulk load could not carry (see ProductService.ListParams). */
  const [missing, setMissing] = useState<'' | 'category' | 'supplier' | 'coefficient'>('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'createdAt',
    dir: 'desc',
  });

  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [editing, setEditing] = useState<Product | 'new' | null>(null);
  const [showImport, setShowImport] = useState(false);
  /**
   * File-level import defaults. A vendor catalogue is one supplier and one margin policy for
   * tens of thousands of rows; without this the supplier could only be set by repeating the code
   * on every line, which is why every product in the catalogue currently has no supplier at all.
   * A per-row column still wins over these.
   */
  const [impSupplierId, setImpSupplierId] = useState('');
  const [impCoefficient, setImpCoefficient] = useState('');
  const [bulkSupplier, setBulkSupplier] = useState(false);
  const [bulkSupplierId, setBulkSupplierId] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [importSuppliers, setImportSuppliers] = useState<
    { id: string; code: string; name: string }[]
  >([]);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set('q', q.trim());
    if (status !== 'all') p.set('active', String(status === 'active'));
    if (categoryId) p.set('categoryId', categoryId);
    if (missing) p.set('missing', missing);
    p.set('sort', sort.key);
    p.set('dir', sort.dir);
    p.set('page', String(page));
    p.set('pageSize', String(PAGE_SIZE));
    return p.toString();
  }, [q, status, categoryId, missing, sort, page]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<ListResponse>(`/products?${queryString}`);
      setRows(data.data);
      setTotal(data.total);
      setSupplierCandidates(data.supplierCandidates ?? []);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [queryString, showApiError]);

  // Debounce reloads so typing in the search box doesn't hammer the API.
  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  /**
   * Write an inline Stock edit as a ledger movement.
   *
   * Stock is never a column anyone sets: it is SUM(stock_ledger), so the typed figure is sent to
   * the SC-13 adjustment endpoint, which books the difference as ADJUSTMENT_PLUS/MINUS, audits it
   * and notifies OWNER/PURCHASER. The server re-derives the delta from the live ledger inside the
   * transaction, so a figure typed against a stale screen still lands as a correction to what is
   * actually there rather than overwriting a sale that happened meanwhile.
   */
  const commitStockEdit = async (row: Product, raw: string) => {
    const next = Number(raw.trim());
    if (raw.trim() === '' || !Number.isInteger(next) || next < 0) {
      showApiError({
        response: {
          data: { code: 'VALIDATION_ERROR', message: t('productAdmin.stockEdit.invalid') },
        },
      });
      return;
    }
    if (next === row.availableStock) return; // nothing to book
    setStockSaving(row.id);
    try {
      const { data } = await apiClient.post<{ availableStock: number; delta: number }>(
        '/warehouse/inventory/adjust',
        { productId: row.id, availableQty: next },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      // Patch the one row rather than reloading: the list is filtered, sorted and paged, and a
      // reload here would move the row out from under the next cell someone means to edit.
      setRows((rs) =>
        rs.map((x) => (x.id === row.id ? { ...x, availableStock: data.availableStock } : x))
      );
      push({
        tone: 'success',
        message: t('productAdmin.stockEdit.done', {
          sku: row.sku,
          from: row.availableStock,
          to: data.availableStock,
          delta: `${data.delta > 0 ? '+' : ''}${data.delta}`,
        }),
      });
    } catch (err) {
      showApiError(err);
    } finally {
      setStockSaving(null);
    }
  };

  useEffect(() => {
    apiClient
      .get<{ id: string; name: string }[]>('/products/categories')
      .then((r) => setCategories(r.data))
      .catch(() => undefined);
    apiClient
      .get<{ id: string; code: string; name: string }[]>('/products/suppliers')
      .then((r) => setImportSuppliers(r.data))
      .catch(() => undefined);
  }, []);

  // Reset to first page whenever a filter changes.
  useEffect(() => {
    setPage(0);
  }, [q, status, categoryId, missing]);

  // Re-submitting the navbar search while already on this page updates ?q= without a remount;
  // mirror it into the filter so the list reloads (the debounced load reacts to q).
  const urlQ = searchParams.get('q') ?? '';
  useEffect(() => {
    setQ(urlQ);
  }, [urlQ]);

  const toggleSort = (key: SortKey) =>
    setSort((cur) =>
      cur.key === key ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );

  const exportCsv = async () => {
    try {
      const p = new URLSearchParams();
      if (q.trim()) p.set('q', q.trim());
      if (status !== 'all') p.set('active', String(status === 'active'));
      if (categoryId) p.set('categoryId', categoryId);
      const res = await apiClient.get(`/products/export.csv?${p.toString()}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showApiError(err);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <PageHeader
        title={t('productAdmin.title')}
        description={t('productAdmin.description')}
        actions={
          <>
            {canEditCatalog && (
              <Button variant="outline" size="sm" onClick={() => setShowImport(true)}>
                <Upload className="h-4 w-4" /> {t('productAdmin.actions.importFile')}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={exportCsv}>
              <Download className="h-4 w-4" /> {t('productAdmin.actions.exportCsv')}
            </Button>
            <Link
              to="/products/categories"
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
            >
              <FolderTree className="h-4 w-4" /> {t('productAdmin.actions.categories')}
            </Link>
            {canEditCatalog && (
              <Button size="sm" onClick={() => setEditing('new')}>
                <Plus className="h-4 w-4" /> {t('productAdmin.actions.newProduct')}
              </Button>
            )}
          </>
        }
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border bg-card p-3 shadow-sm">
        <div className="relative min-w-[16rem] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('productAdmin.searchPlaceholder')}
          />
        </div>
        <Select
          className="w-auto"
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
        >
          <option value="all">{t('productAdmin.filters.allStatuses')}</option>
          <option value="active">{t('productAdmin.active')}</option>
          <option value="inactive">{t('productAdmin.inactive')}</option>
        </Select>
        <Select
          className="w-auto max-w-[14rem]"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
        >
          <option value="">{t('productAdmin.filters.allCategories')}</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        {/*
          The remainder the backfill leaves behind, made workable. The category backfill matches
          ~78% of the catalogue; supplier can't be derived from TecDoc at all. Without a filter,
          "what's still missing" is a number in a script's output that nobody can act on.
        */}
        <Select
          className="w-auto"
          value={missing}
          onChange={(e) => setMissing(e.target.value as typeof missing)}
        >
          <option value="">{t('productAdmin.filters.completeOrNot')}</option>
          <option value="category">{t('productAdmin.filters.missingCategory')}</option>
          <option value="supplier">{t('productAdmin.filters.missingSupplier')}</option>
          <option value="coefficient">{t('productAdmin.filters.noOwnCoefficient')}</option>
        </Select>
        {missing === 'supplier' && canEditCatalog && (
          <Button size="sm" variant="outline" onClick={() => setBulkSupplier(true)}>
            {t('productAdmin.bulk.assignToThese')}
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
        <div className="flex items-center justify-between border-b px-3 py-2 text-sm text-muted-foreground">
          <span>{t('productAdmin.productCount', { count: total })}</span>
        </div>
        <div className="relative w-full overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <Th
                  label={t('productAdmin.columns.sku')}
                  sortKey="sku"
                  sort={sort}
                  onSort={toggleSort}
                />
                <Th
                  label={t('productAdmin.columns.name')}
                  sortKey="name"
                  sort={sort}
                  onSort={toggleSort}
                />
                <Th
                  label={t('productAdmin.columns.brand')}
                  sortKey="brand"
                  sort={sort}
                  onSort={toggleSort}
                />
                <th className="h-10 px-3 text-left align-middle font-medium text-muted-foreground">
                  {t('productAdmin.columns.category')}
                </th>
                <Th
                  label={t('productAdmin.columns.stock')}
                  sortKey="availableStock"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                />
                <Th
                  label={t('productAdmin.columns.purchase')}
                  sortKey="purchasePriceHT"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                />
                <Th
                  label={t('productAdmin.columns.status')}
                  sortKey="active"
                  sort={sort}
                  onSort={toggleSort}
                />
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {loading && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                    {t('productAdmin.loading')}
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-10 text-center text-muted-foreground">
                    {t('productAdmin.noMatch')}
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setEditing(r)}
                    className="cursor-pointer border-t transition-colors hover:bg-muted/50"
                  >
                    <td className="p-3 align-middle">
                      <div className="truncate max-w-[10rem] font-mono text-xs" title={r.sku}>
                        {r.sku}
                      </div>
                    </td>
                    <td className="p-3 align-middle">
                      <div className="truncate max-w-[18rem] font-medium" title={r.name}>
                        {r.name}
                      </div>
                      {r.manufacturerRef && (
                        <div
                          className="truncate max-w-[18rem] text-xs text-muted-foreground"
                          title={r.manufacturerRef}
                        >
                          {r.manufacturerRef}
                        </div>
                      )}
                    </td>
                    <td className="p-3 align-middle">
                      <div className="truncate max-w-[18rem]" title={r.brand ?? undefined}>
                        {r.brand ?? '—'}
                      </div>
                    </td>
                    <td className="p-3 align-middle text-muted-foreground">
                      <div className="truncate max-w-[18rem]" title={r.categoryName ?? undefined}>
                        {r.categoryName ?? '—'}
                      </div>
                      {/* This list is the only place a product filed under a deactivated category
                          still shows up — so it has to say why nobody can find it anywhere else. */}
                      {r.categoryVisible === false && (
                        <span
                          className="mt-0.5 inline-block rounded bg-destructive/10 px-1 text-[10px] text-destructive"
                          title={t('productAdmin.categoryOffTitle')}
                        >
                          {t('productAdmin.categoryOff')}
                        </span>
                      )}
                    </td>
                    {/* Stock is editable in place. The click must not reach the row, which opens
                        the product drawer. */}
                    <td
                      className="p-3 align-middle text-right tabular-nums"
                      onClick={(e) => canAdjustStock && e.stopPropagation()}
                    >
                      {stockEdit?.id === r.id ? (
                        <input
                          autoFocus
                          type="number"
                          step="1"
                          min="0"
                          value={stockEdit.value}
                          aria-label={t('productAdmin.stockEdit.aria', { sku: r.sku })}
                          onChange={(e) => setStockEdit({ id: r.id, value: e.target.value })}
                          onKeyDown={(e) => {
                            // Both keys leave through blur, which is the single commit point —
                            // handling Enter here as well would book the movement twice.
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              e.currentTarget.blur();
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              stockCancelled.current = true;
                              e.currentTarget.blur();
                            }
                          }}
                          onBlur={() => {
                            const cancelled = stockCancelled.current;
                            stockCancelled.current = false;
                            const typed = stockEdit.value;
                            setStockEdit(null);
                            if (!cancelled) void commitStockEdit(r, typed);
                          }}
                          className={cn(inputClass, 'h-7 w-20 px-2 py-0 text-right tabular-nums')}
                        />
                      ) : (
                        <button
                          type="button"
                          disabled={!canAdjustStock || stockSaving === r.id}
                          onClick={() =>
                            setStockEdit({ id: r.id, value: String(r.availableStock) })
                          }
                          title={canAdjustStock ? t('productAdmin.stockEdit.title') : undefined}
                          className={cn(
                            'rounded px-2 py-0.5 tabular-nums',
                            canAdjustStock &&
                              'hover:bg-muted hover:ring-1 hover:ring-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                            stockSaving === r.id && 'opacity-50',
                            r.availableStock <= (r.lowStockThreshold ?? 0) && 'text-destructive'
                          )}
                        >
                          {r.availableStock}
                        </button>
                      )}
                    </td>
                    <td className="p-3 align-middle text-right tabular-nums">
                      {euro(r.purchasePriceHT)}
                    </td>
                    <td className="p-3 align-middle">
                      {r.active ? (
                        <Badge variant="success">{t('productAdmin.active')}</Badge>
                      ) : (
                        <Badge variant="muted">{t('productAdmin.inactive')}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
          <span>{t('productAdmin.pagination.page', { page: page + 1, total: totalPages })}</span>
          <div className="flex gap-1">
            <button
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="inline-flex h-7 items-center rounded-md border border-input bg-background px-2 transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> {t('productAdmin.pagination.prev')}
            </button>
            <button
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              className="inline-flex h-7 items-center rounded-md border border-input bg-background px-2 transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-40"
            >
              {t('productAdmin.pagination.next')} <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Nothing in our catalogue, but a supplier can deliver it. These are live quotes, not
          records — hence a separate block whose only action is to create the product. */}
      {!loading && supplierCandidates.length > 0 && (
        <div className="mt-4 rounded-lg border border-warning/40 bg-warning/5">
          <div className="border-b border-warning/30 px-3 py-2 text-sm font-semibold">
            {t('productAdmin.candidates.title')}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:text-left">
                  <th>{t('productAdmin.candidates.reference')}</th>
                  <th>{t('productAdmin.candidates.name')}</th>
                  <th>{t('productAdmin.candidates.brand')}</th>
                  <th>{t('productAdmin.candidates.supplier')}</th>
                  <th>{t('productAdmin.candidates.available')}</th>
                  <th>{t('productAdmin.candidates.lead')}</th>
                  <th className="text-right">{t('productAdmin.candidates.price')}</th>
                  <th />
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {supplierCandidates.map((c) => (
                  <tr key={c.id} className="border-t border-warning/20">
                    <td className="px-3 py-2 font-mono">{c.sku}</td>
                    <td className="px-3 py-2">{c.name}</td>
                    <td className="px-3 py-2">{c.brand ?? '—'}</td>
                    <td className="px-3 py-2">{c.supplier?.supplierName ?? '—'}</td>
                    <td className="px-3 py-2">{c.supplier?.availableQty ?? '—'}</td>
                    <td className="px-3 py-2">
                      {c.supplier?.leadTimeDays == null
                        ? '—'
                        : c.supplier.leadTimeDays === 0
                          ? t('productAdmin.candidates.today')
                          : t('productAdmin.candidates.leadDays', {
                              days: c.supplier.leadTimeDays,
                            })}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {c.supplier?.indicativeSellPriceHT != null && (
                        <span title={t('productAdmin.candidates.indicativeTitle')}>
                          ≈ {formatEUR(c.supplier.indicativeSellPriceHT)} HT
                        </span>
                      )}
                      {c.supplier?.unitPriceHT != null && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          {t('productAdmin.candidates.cost', {
                            cost: c.supplier.unitPriceHT.toFixed(2),
                          })}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => setCreatingFrom(c)}
                        className="inline-flex h-7 items-center rounded-md border border-input bg-background px-2 text-xs transition-colors hover:bg-accent hover:text-accent-foreground"
                      >
                        {t('productAdmin.candidates.create')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <ProductDrawer
          product={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(msg) => {
            push({ tone: 'success', message: msg });
            setEditing(null);
            void load();
          }}
          onReload={() => void load()}
          onError={showApiError}
        />
      )}

      {creatingFrom && (
        <ProductDrawer
          product={null}
          prefill={{
            // The manufacturer reference doubles as the initial SKU — unique, recognisable, and
            // editable before saving.
            sku: creatingFrom.manufacturerRef ?? creatingFrom.sku,
            name: creatingFrom.name,
            brand: creatingFrom.brand ?? '',
            manufacturerRef: creatingFrom.manufacturerRef ?? creatingFrom.sku,
            ...(creatingFrom.supplier?.unitPriceHT != null
              ? { purchasePriceHT: String(creatingFrom.supplier.unitPriceHT) }
              : {}),
          }}
          onClose={() => setCreatingFrom(null)}
          onSaved={(msg) => {
            push({ tone: 'success', message: msg });
            setCreatingFrom(null);
            void load();
          }}
          onReload={() => void load()}
          onError={showApiError}
        />
      )}

      {/*
        Bulk supplier assignment. Scoped to the CURRENT brand/category filter and to products
        that have no supplier yet, so the set being changed is the set on screen. Re-pointing a
        product that already has a supplier stays a per-product edit — a filter should not be
        able to redirect purchasing for thousands of rows in one click.
      */}
      {bulkSupplier && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md space-y-3 rounded-lg border bg-card p-4 shadow-lg">
            <h2 className="font-semibold">{t('productAdmin.bulk.title')}</h2>
            <p className="text-xs text-muted-foreground">
              {categoryId
                ? t('productAdmin.bulk.descriptionInCategory')
                : t('productAdmin.bulk.description')}
            </p>
            <Select value={bulkSupplierId} onChange={(e) => setBulkSupplierId(e.target.value)}>
              <option value="">{t('productAdmin.bulk.selectSupplier')}</option>
              {importSuppliers.map((sup) => (
                <option key={sup.id} value={sup.id}>
                  {sup.code} · {sup.name}
                </option>
              ))}
            </Select>
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setBulkSupplier(false);
                  setBulkSupplierId('');
                }}
              >
                {t('productAdmin.cancel')}
              </Button>
              <Button
                size="sm"
                disabled={!bulkSupplierId || bulkBusy}
                onClick={async () => {
                  setBulkBusy(true);
                  try {
                    const { data } = await apiClient.post<{ updated: number }>(
                      '/products/bulk/supplier',
                      {
                        supplierId: bulkSupplierId,
                        ...(categoryId ? { categoryId } : {}),
                      },
                      { headers: { 'X-Command-Id': crypto.randomUUID() } }
                    );
                    push({
                      tone: 'success',
                      message: t('productAdmin.bulk.done', { count: data.updated }),
                    });
                    setBulkSupplier(false);
                    setBulkSupplierId('');
                    void load();
                  } catch (err) {
                    showApiError(err);
                  } finally {
                    setBulkBusy(false);
                  }
                }}
              >
                {bulkBusy ? t('productAdmin.bulk.assigning') : t('productAdmin.bulk.assign')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ImportDialog
        open={showImport}
        onClose={() => setShowImport(false)}
        onDone={() => void load()}
        title={t('productAdmin.import.title')}
        description={t('productAdmin.import.description')}
        modeLabel={t('productAdmin.import.modeLabel')}
        modes={[
          {
            value: 'NEW_PRODUCTS',
            label: t('productAdmin.import.modes.new'),
            hint: t('productAdmin.import.modes.newHint'),
          },
          {
            value: 'PRICE_UPDATE',
            label: t('productAdmin.import.modes.price'),
            hint: t('productAdmin.import.modes.priceHint'),
          },
        ]}
        templateHeader="No,Brand,Category name,Category code,Category id,Supplier,Coefficient,Net Price EXW,Stock"
        templateName="product-import-template.csv"
        exampleRows={PRODUCT_IMPORT_EXAMPLE}
        exampleName="product-import-example.csv"
        previewEndpoint="/products/import/preview"
        commitEndpoint="/products/import/commit"
        buildBody={(mode, fileBase64) => ({
          mode,
          fileBase64,
          // Only NEW_PRODUCTS writes these; PRICE_UPDATE reports them as ignored columns, so
          // sending them there would advertise an effect the mode deliberately does not have.
          ...(mode === 'NEW_PRODUCTS' && (impSupplierId || impCoefficient.trim())
            ? {
                defaults: {
                  ...(impSupplierId ? { mainSupplierId: impSupplierId } : {}),
                  ...(impCoefficient.trim() ? { coefficient: Number(impCoefficient) } : {}),
                },
              }
            : {}),
        })}
        extraControls={(mode) =>
          mode === 'NEW_PRODUCTS' ? (
            <div className="rounded border bg-muted/30 p-3 space-y-2">
              <p className="text-xs text-muted-foreground">
                {t('productAdmin.import.defaultsIntro')}
              </p>
              <div className="flex flex-wrap gap-3">
                <label className="text-xs space-y-1">
                  <span className="block text-muted-foreground">
                    {t('productAdmin.import.supplierForFile')}
                  </span>
                  <select
                    value={impSupplierId}
                    onChange={(e) => setImpSupplierId(e.target.value)}
                    className={inputClass + ' w-auto'}
                  >
                    <option value="">{t('productAdmin.none')}</option>
                    {importSuppliers.map((sup) => (
                      <option key={sup.id} value={sup.id}>
                        {sup.code} · {sup.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs space-y-1">
                  <span className="block text-muted-foreground">
                    {t('productAdmin.import.coefficient')}
                  </span>
                  <input
                    value={impCoefficient}
                    onChange={(e) => setImpCoefficient(e.target.value)}
                    placeholder={t('productAdmin.inherit')}
                    inputMode="decimal"
                    className={inputClass + ' w-32'}
                  />
                </label>
              </div>
              {impCoefficient.trim() !== '' && !(Number(impCoefficient) > 1) && (
                <p className="text-xs text-destructive">
                  {t('productAdmin.import.coefficientTooLow')}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {t('productAdmin.import.coefficientBlank')}
              </p>
            </div>
          ) : null
        }
        showAutoEnrich
        statusVariant={catalogStatusVariant}
        actionable={(s) => s === 'NEW' || s === 'UPDATE'}
        renderDetail={renderCatalogDetail}
        summarizeResult={(raw) => {
          const d = raw as CatalogImportResult;
          const parts: string[] = [];
          if (d.created) parts.push(t('productAdmin.import.result.created', { count: d.created }));
          if (d.updated) parts.push(t('productAdmin.import.result.updated', { count: d.updated }));
          if (d.skipped) parts.push(t('productAdmin.import.result.skipped', { count: d.skipped }));
          if (d.failed) parts.push(t('productAdmin.import.result.failed', { count: d.failed }));
          const warn = d.warnings?.length
            ? ' ' + t('productAdmin.import.result.warnings', { count: d.warnings.length })
            : '';
          return {
            tone: d.failed > 0 ? 'warning' : 'success',
            title: t('productAdmin.import.result.title', { count: d.total }),
            message: (parts.join(', ') || t('productAdmin.import.result.noChanges')) + '.' + warn,
          };
        }}
      />
    </div>
  );
}

/** Preview detail cell for the catalog import (NEW shows the new product, UPDATE the price diff). */
function renderCatalogDetail(row: ImportPreviewRow) {
  if (row.status === 'NEW') {
    const inc = row.incoming as
      | { name?: string; brand?: string | null; purchasePriceHT?: number }
      | undefined;
    const enrich = row.enrich as { candidates: number } | null | undefined;
    return (
      <span>
        {inc?.name || row.ref}
        {inc?.purchasePriceHT != null && ` · ${formatEUR(inc.purchasePriceHT)}`}
        {enrich && enrich.candidates > 0 && (
          <span className="text-muted-foreground">
            {' · '}
            {i18n.t('productAdmin.import.tecdocMatches', { count: enrich.candidates })}
          </span>
        )}
      </span>
    );
  }
  if (row.status === 'UPDATE' || row.status === 'NO_CHANGE') {
    const cur = row.current as { purchasePriceHT?: number } | undefined;
    const inc = row.incoming as { purchasePriceHT?: number } | undefined;
    return (
      <span className="font-mono">
        {cur?.purchasePriceHT != null ? `${formatEUR(cur.purchasePriceHT)}` : '—'}
        {inc?.purchasePriceHT != null && ` → ${formatEUR(inc.purchasePriceHT)}`}
      </span>
    );
  }
  if (row.status === 'DUPLICATE') {
    const cur = row.current as { name?: string } | undefined;
    return <span className="text-muted-foreground">{cur?.name}</span>;
  }
  return null;
}

function Th({
  label,
  sortKey,
  sort,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; dir: 'asc' | 'desc' };
  onSort: (k: SortKey) => void;
  align?: 'left' | 'right';
}) {
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={cn(
        'h-10 cursor-pointer select-none px-3 align-middle font-medium text-muted-foreground hover:text-foreground',
        align === 'right' ? 'text-right' : 'text-left'
      )}
    >
      {label}
      {sort.key === sortKey && (
        <span className="ml-1 text-xs">{sort.dir === 'asc' ? '▲' : '▼'}</span>
      )}
    </th>
  );
}

// ---------------------------------------------------------------------------
// Create / edit slide-over
// ---------------------------------------------------------------------------

interface FormState {
  sku: string;
  name: string;
  shortName: string;
  brand: string;
  categoryId: string;
  ean: string;
  manufacturerRef: string;
  origin: string;
  purchasePriceHT: string;
  coefficient: string;
  sellingPriceOverride: string;
  vatRate: string;
  lowStockThreshold: string;
  packagingUnit: string;
  weightKg: string;
  description: string;
  active: boolean;
  // ---- extended "Add Product" sheet fields (all held as strings for the inputs) ----
  maxStockThreshold: string;
  ean2: string;
  mainSupplierId: string;
  subGroupCode: string;
  productGroupId: string;
  attributeId: string;
  fileId: string;
  referenceType1: string;
  reference2: string;
  referenceType2: string;
  reference3: string;
  referenceType3: string;
  reference4: string;
  referenceType4: string;
  productKeys: string;
  heightMm: string;
  lengthMm: string;
  widthMm: string;
  inch: string;
  packingMaterial: string;
  labelType: string;
  photoUrl: string;
  popularityCode: string;
  statusCode: string;
  returnCode: string;
  currency1: string;
  currency2: string;
  groupCodeDiscount2: string;
  groupCodeIn1: string;
  groupCodeIn2: string;
  groupCodeEx1: string;
  groupCodeEx2: string;
  minOrderQty: string;
  conditioningQty: string;
  disposalFee: string;
  depositAmountHt: string;
  environmentalSurcharge: string;
  grossWeightKg: string;
}

// Secondary-reference type codes (mirrors backend REFERENCE_TYPES).
const REFERENCE_TYPES = ['EAN', 'VC', 'SA', 'AL', 'IN', 'OE', 'WVA'] as const;

const numStr = (v: number | null | undefined) => (v != null ? String(v) : '');

function toForm(p: Product | null): FormState {
  return {
    sku: p?.sku ?? '',
    name: p?.name ?? '',
    shortName: p?.shortName ?? '',
    brand: p?.brand ?? '',
    categoryId: p?.categoryId ?? '',
    ean: p?.ean ?? '',
    manufacturerRef: p?.manufacturerRef ?? '',
    origin: p?.origin ?? 'AFTERMARKET',
    purchasePriceHT: p ? String(p.purchasePriceHT) : '',
    coefficient: p?.coefficient != null ? String(p.coefficient) : '',
    sellingPriceOverride: p?.sellingPriceOverride != null ? String(p.sellingPriceOverride) : '',
    vatRate: p?.vatRate != null ? String(p.vatRate) : '20',
    lowStockThreshold: p?.lowStockThreshold != null ? String(p.lowStockThreshold) : '5',
    packagingUnit: p?.packagingUnit ?? 'PIECE',
    weightKg: p?.weightKg != null ? String(p.weightKg) : '',
    description: p?.description ?? '',
    active: p?.active ?? true,
    maxStockThreshold: numStr(p?.maxStockThreshold),
    ean2: p?.ean2 ?? '',
    mainSupplierId: p?.mainSupplierId ?? '',
    subGroupCode: p?.subGroupCode ?? '',
    productGroupId: p?.productGroupId ?? '',
    attributeId: p?.attributeId ?? '',
    fileId: p?.fileId ?? '',
    referenceType1: p?.referenceType1 ?? '',
    reference2: p?.reference2 ?? '',
    referenceType2: p?.referenceType2 ?? '',
    reference3: p?.reference3 ?? '',
    referenceType3: p?.referenceType3 ?? '',
    reference4: p?.reference4 ?? '',
    referenceType4: p?.referenceType4 ?? '',
    productKeys: p?.productKeys ?? '',
    heightMm: numStr(p?.heightMm),
    lengthMm: numStr(p?.lengthMm),
    widthMm: numStr(p?.widthMm),
    inch: numStr(p?.inch),
    packingMaterial: p?.packingMaterial ?? '',
    labelType: p?.labelType ?? '',
    photoUrl: p?.photoUrl ?? '',
    popularityCode: p?.popularityCode ?? '',
    statusCode: p?.statusCode ?? '',
    returnCode: p?.returnCode ?? '',
    currency1: p?.currency1 ?? '',
    currency2: p?.currency2 ?? '',
    groupCodeDiscount2: p?.groupCodeDiscount2 ?? '',
    groupCodeIn1: p?.groupCodeIn1 ?? '',
    groupCodeIn2: p?.groupCodeIn2 ?? '',
    groupCodeEx1: p?.groupCodeEx1 ?? '',
    groupCodeEx2: p?.groupCodeEx2 ?? '',
    minOrderQty: numStr(p?.minOrderQty),
    conditioningQty: numStr(p?.conditioningQty),
    disposalFee: numStr(p?.disposalFee),
    depositAmountHt: numStr(p?.depositAmountHt),
    environmentalSurcharge: numStr(p?.environmentalSurcharge),
    grossWeightKg: numStr(p?.grossWeightKg),
  };
}

// ---------------------------------------------------------------------------
// TecDoc article → form fields
// ---------------------------------------------------------------------------

/** Strip accents + the bracketed unit TecDoc appends to a criterion name ("Hauteur [mm]"). */
const specBase = (label: string) =>
  label
    .replace(/\[[^\]]*\]/g, ' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const normUnit = (u: string | null) =>
  (u ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const MM_PER: Record<string, number> = { mm: 1, cm: 10, m: 1000, in: 25.4 };
const KG_PER: Record<string, number> = { kg: 1, g: 0.001, t: 1000 };
const INCH_UNITS = new Set(['pouce', 'pouces', 'inch', 'inches', 'zoll', '"']);

/**
 * The handful of TecDoc criteria that have a real home on this sheet.
 *
 * Matched on the criterion's BASE name and only on an EXACT base: "Hauteur" fills Height, while
 * "Hauteur de construction", "Hauteur sans pôle" or "Longueur d'enveloppe" deliberately do not.
 * A near-miss dimension written into a dimension field is worse than a blank one — nobody would
 * ever catch it. The unit column carries the scale, so a cm/inch row converts instead of landing
 * as a wrong number, and a criterion whose unit we can't convert is skipped rather than guessed.
 * Everything else TecDoc knows stays in `specifications`, which the enricher stores as-is.
 */
const SPEC_TARGETS: {
  field: 'heightMm' | 'lengthMm' | 'widthMm' | 'weightKg' | 'inch';
  bases: string[];
  scale: 'mm' | 'kg' | 'inch';
}[] = [
  { field: 'heightMm', bases: ['hauteur', 'height'], scale: 'mm' },
  { field: 'lengthMm', bases: ['longueur', 'length'], scale: 'mm' },
  { field: 'widthMm', bases: ['largeur', 'width'], scale: 'mm' },
  { field: 'weightKg', bases: ['poids', 'weight'], scale: 'kg' },
  { field: 'inch', bases: ['diametre', 'diameter'], scale: 'inch' },
];

/** Technical criteria → the sheet's numeric inputs. First match wins (TecDoc criterion order). */
function specsToFields(specs: Spec[]): Partial<FormState> {
  const out: Partial<FormState> = {};
  for (const spec of specs) {
    const base = specBase(spec.label);
    const target = SPEC_TARGETS.find((t) => t.bases.includes(base));
    if (!target || out[target.field] != null) continue;
    // TecDoc values are free text ("12,5", "45 mm") — take the leading number, comma or dot.
    const num = Number((spec.value.match(/-?\d+(?:[.,]\d+)?/)?.[0] ?? '').replace(',', '.'));
    if (!Number.isFinite(num)) continue;
    const unit = normUnit(spec.unit);
    const factor =
      target.scale === 'mm'
        ? (MM_PER[unit] ?? (INCH_UNITS.has(unit) ? MM_PER.in : undefined))
        : target.scale === 'kg'
          ? KG_PER[unit]
          : INCH_UNITS.has(unit)
            ? 1
            : undefined;
    if (factor === undefined) continue;
    out[target.field] = String(Math.round(num * factor * 1000) / 1000);
  }
  return out;
}

/** Every form value a TecDoc article can supply. The caller decides blank-vs-overwrite. */
function previewToForm(preview: ArticlePreview): Partial<FormState> {
  const s = preview.snapshot;
  return {
    sku: s.articleNr,
    name: s.designation ?? '',
    brand: s.brand,
    manufacturerRef: s.articleNr,
    ean: s.eans[0] ?? '',
    categoryId: preview.category?.id ?? '',
    // The secondary-reference pairs are exactly the home for an OE number; cross/trade refs have
    // no matching type code, so they stay in the (server-stored) crossReferences list.
    ...(s.oemReferences[0] ? { reference2: s.oemReferences[0], referenceType2: 'OE' } : {}),
    // Only an ABSOLUTE manufacturer link belongs in a URL field. Most TecDoc imagery is a
    // licensed asset we serve ourselves, stored as a "<supplierId>/<file>" key — that key lives
    // in image_urls (written by the enricher) and would be meaningless typed into Photo (URL).
    photoUrl: s.imageUrls.find((u) => !isHtmlAsset(u) && /^https?:\/\//i.test(u)) ?? '',
    ...specsToFields(s.specifications),
  };
}

/**
 * Merge TecDoc values into the sheet under the SAME rule the enricher applies server-side: fill
 * blanks only, unless the operator ticked Overwrite. Keeping one rule on both sides means the
 * preview an operator accepts on a new product and the write that lands on an existing one can
 * never disagree about what TecDoc is allowed to touch.
 */
function mergePreview(
  form: FormState,
  preview: ArticlePreview,
  opts: { overwrite: boolean; allowSku: boolean }
): FormState {
  const next = { ...form };
  for (const [key, value] of Object.entries(previewToForm(preview)) as [
    keyof FormState,
    string,
  ][]) {
    if (!value) continue;
    // TecDoc never renames an EXISTING product: the operator can edit the reference by hand,
    // but an enrichment run must not silently re-key the catalogue under them.
    if (key === 'sku' && !opts.allowSku) continue;
    if (!opts.overwrite && String(next[key] ?? '').trim() !== '') continue;
    (next as unknown as Record<string, string>)[key] = value;
  }
  return next;
}

function ProductDrawer({
  product,
  prefill,
  onClose,
  onSaved,
  onReload,
  onError,
}: {
  product: Product | null;
  /** Seed values for a NEW product (e.g. from a supplier quote). Every field stays editable. */
  prefill?: Partial<FormState>;
  onClose: () => void;
  onSaved: (msg: string) => void;
  onReload?: () => void;
  onError: (err: unknown) => void;
}) {
  const { t } = useTranslation();
  // `current` tracks the live product (refreshed after enrichment); `product` is the initial.
  const [current, setCurrent] = useState<Product | null>(product);
  const isNew = current === null;
  const [form, setForm] = useState<FormState>(() => ({ ...toForm(product), ...(prefill ?? {}) }));
  /**
   * The cascade resolved for the CURRENT form state, so an empty coefficient field reads as
   * "inherits 2.50 from the global default" instead of looking like a gap someone forgot to
   * fill. That legibility is the reason the field stays optional: a blank coefficient keeps the
   * product steerable from the category grid, and typing a number on every product would give
   * that up permanently for 50,000 rows.
   */
  const [resolvedCoef, setResolvedCoef] = useState<{
    value: number;
    source: string;
    label: string;
  } | null>(null);
  useEffect(() => {
    const params = new URLSearchParams();
    if (current?.id) params.set('productId', current.id);
    if (form.categoryId) params.set('categoryId', form.categoryId);
    if (form.coefficient.trim()) params.set('coefficient', form.coefficient.trim());
    let cancelled = false;
    apiClient
      .get<{ value: number; source: string; label: string }>(
        `/products/resolve-coefficient?${params.toString()}`
      )
      .then((r) => {
        if (!cancelled) setResolvedCoef(r.data);
      })
      .catch(() => {
        if (!cancelled) setResolvedCoef(null);
      });
    return () => {
      cancelled = true;
    };
  }, [current?.id, form.categoryId, form.coefficient]);

  /** Selling price the catalogue will compute — override if set, else purchase × effective coef. */
  const effectiveSelling = useMemo(() => {
    const purchase = Number(form.purchasePriceHT || 0);
    const override = form.sellingPriceOverride.trim();
    if (override !== '' && Number.isFinite(Number(override))) return Number(override);
    if (!resolvedCoef || !(purchase > 0)) return null;
    return Math.round(purchase * resolvedCoef.value * 100) / 100;
  }, [form.purchasePriceHT, form.sellingPriceOverride, resolvedCoef]);

  // Display label for the attached category (the id lives in form.categoryId).
  const [categoryLabel, setCategoryLabel] = useState(product?.categoryName ?? '');
  const [pickCat, setPickCat] = useState(false);
  const [saving, setSaving] = useState(false);
  const canEnrich = usePermission(PermissionName.CATALOG_WRITE);
  // Purchase price is a separately-gated field: only OWNER/ADMIN/PURCHASER may write it. A MANAGER
  // edits everything else but the cost stays read-only and is omitted from the payload (the backend
  // 403s on a purchasePriceHT it isn't allowed to set, which would otherwise block the whole save).
  const canWriteCost = usePermission(PermissionName.PRODUCT_COST_WRITE);
  // A cost-only editor (PURCHASER: PRODUCT_COST_WRITE without CATALOG_WRITE) may read the sheet and
  // change only the purchase price — every other field is locked and dropped from the payload.
  const lockSheet = !canEnrich;
  // The Product Reference is editable on an existing product too (fixing a typo, or re-keying a
  // quick-created `SKU-…` stub). It is the catalogue's identity, so the change is flagged rather
  // than made silently.
  const skuRenamed = !isNew && !!current && form.sku.trim() !== current.sku;
  const firstField = useRef<HTMLInputElement>(null);
  // Pre-save verification (create only): duplicates found by /products/validate, held for the
  // operator to confirm before the product is actually created.
  const [dupConfirm, setDupConfirm] = useState<{
    duplicates: DuplicateHit[];
    enrich: ArticleMatch[];
  } | null>(null);
  // Suppliers for the Supplier picker (main_supplier_id FK). Best-effort: an empty list just
  // leaves the dropdown with only the "— none —" option.
  const [suppliers, setSuppliers] = useState<{ id: string; code: string; name: string }[]>([]);
  useEffect(() => {
    apiClient
      .get<{ id: string; code: string; name: string }[]>('/products/suppliers')
      .then((r) => setSuppliers(r.data))
      .catch(() => setSuppliers([]));
  }, []);

  /**
   * The TecDoc article this sheet was filled from, held until the product exists.
   *
   * On a NEW product the Enrich button fills the inputs from a read-only preview — the operator
   * has to be able to see and correct TecDoc's values before anything is created. The snapshot
   * side of enrichment (OE/cross refs, images, specs, vehicle fitment) still needs a product id,
   * so it runs against the created row on save; this is what remembers which article to run.
   */
  const [pendingArt, setPendingArt] = useState<ArticlePreview | null>(null);
  // Article number of the last fill, for the "review and save" hint under the panel.
  const [filledFrom, setFilledFrom] = useState<string | null>(null);

  /** Fill the sheet from a TecDoc preview (new product — nothing written server-side yet). */
  const applyPreview = (preview: ArticlePreview, overwrite: boolean): void => {
    setForm((f) => mergePreview(f, preview, { overwrite, allowSku: true }));
    if (preview.category && (overwrite || !form.categoryId))
      setCategoryLabel(preview.category.name);
    setPendingArt(preview);
    setFilledFrom(preview.snapshot.articleNr);
  };

  // Existing product: the server already wrote the snapshot, so re-fetch to pick it up, then lay
  // the same TecDoc values over the form — that also fills the fields the enricher doesn't own
  // (dimensions, weight, photo), which stay dirty until the operator saves. Refreshes the list
  // too (the name may have changed). Keeps the drawer open.
  const applyEnriched = async (
    productId: string,
    snapshot: ArticleSnapshot,
    overwrite: boolean
  ): Promise<void> => {
    const { data } = await apiClient.get<Product>(`/products/${productId}`);
    setCurrent(data);
    setForm(
      mergePreview(toForm(data), { snapshot, category: null }, { overwrite, allowSku: false })
    );
    // Enrichment may auto-classify the product (article → generic article → category).
    setCategoryLabel(data.categoryName ?? '');
    setFilledFrom(snapshot.articleNr);
    onReload?.();
  };

  useEffect(() => {
    firstField.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const textOrNull = (v: string) => (v.trim() === '' ? null : v.trim());
  const numOrNull = (v: string) => (v.trim() === '' ? null : Number(v));

  const buildPayload = (): Record<string, unknown> => {
    // Cost-only editor: the backend 403s on any non-cost field, so send the price and nothing else.
    if (lockSheet) return { purchasePriceHT: Number(form.purchasePriceHT || 0) };
    return {
      // Only when it actually changed: an untouched edit must not rewrite the identity column
      // (and a cost-only caller would be 403'd for a field it never meant to send).
      ...(current && form.sku.trim() !== current.sku ? { sku: form.sku.trim() } : {}),
      name: form.name.trim(),
      shortName: textOrNull(form.shortName),
      brand: textOrNull(form.brand),
      categoryId: form.categoryId || null,
      ean: textOrNull(form.ean),
      manufacturerRef: textOrNull(form.manufacturerRef),
      origin: form.origin,
      // Only send the cost when allowed — otherwise the backend rejects the whole request.
      ...(canWriteCost ? { purchasePriceHT: Number(form.purchasePriceHT || 0) } : {}),
      coefficient: numOrNull(form.coefficient),
      sellingPriceOverride: numOrNull(form.sellingPriceOverride),
      vatRate: Number(form.vatRate || 20),
      lowStockThreshold: Number(form.lowStockThreshold || 0),
      packagingUnit: form.packagingUnit,
      weightKg: numOrNull(form.weightKg),
      description: textOrNull(form.description),
      active: form.active,
      // ---- extended "Add Product" sheet fields ----
      maxStockThreshold: numOrNull(form.maxStockThreshold),
      ean2: textOrNull(form.ean2),
      mainSupplierId: form.mainSupplierId || null,
      subGroupCode: textOrNull(form.subGroupCode),
      productGroupId: textOrNull(form.productGroupId),
      attributeId: textOrNull(form.attributeId),
      fileId: textOrNull(form.fileId),
      referenceType1: textOrNull(form.referenceType1),
      reference2: textOrNull(form.reference2),
      referenceType2: textOrNull(form.referenceType2),
      reference3: textOrNull(form.reference3),
      referenceType3: textOrNull(form.referenceType3),
      reference4: textOrNull(form.reference4),
      referenceType4: textOrNull(form.referenceType4),
      productKeys: textOrNull(form.productKeys),
      heightMm: numOrNull(form.heightMm),
      lengthMm: numOrNull(form.lengthMm),
      widthMm: numOrNull(form.widthMm),
      inch: numOrNull(form.inch),
      packingMaterial: textOrNull(form.packingMaterial),
      labelType: textOrNull(form.labelType),
      photoUrl: textOrNull(form.photoUrl),
      popularityCode: textOrNull(form.popularityCode),
      statusCode: textOrNull(form.statusCode),
      returnCode: textOrNull(form.returnCode),
      currency1: textOrNull(form.currency1),
      currency2: textOrNull(form.currency2),
      groupCodeDiscount2: textOrNull(form.groupCodeDiscount2),
      groupCodeIn1: textOrNull(form.groupCodeIn1),
      groupCodeIn2: textOrNull(form.groupCodeIn2),
      groupCodeEx1: textOrNull(form.groupCodeEx1),
      groupCodeEx2: textOrNull(form.groupCodeEx2),
      minOrderQty: numOrNull(form.minOrderQty),
      conditioningQty: numOrNull(form.conditioningQty),
      disposalFee: numOrNull(form.disposalFee),
      depositAmountHt: numOrNull(form.depositAmountHt),
      environmentalSurcharge: numOrNull(form.environmentalSurcharge),
      grossWeightKg: numOrNull(form.grossWeightKg),
    };
  };

  // Surface a client-side validation failure with the same toast shape the API uses, so it reads
  // as "VALIDATION_ERROR" rather than a generic internal error.
  const showValidation = (message: string) =>
    onError({ response: { data: { code: 'VALIDATION_ERROR', message } } });

  // Mirror the backend margin rule (PricingService STEP 6): the Catalog page prices every product
  // for an anonymous customer and throws NEGATIVE_MARGIN when selling ≤ purchase. Block saves that
  // would make a product un-priceable there. Selling = override if set, else purchase × coefficient.
  const validate = (): string | null => {
    if (!form.name.trim()) return t('productAdmin.validation.nameRequired');
    if (!form.sku.trim()) return t('productAdmin.validation.skuRequired');

    /*
     * Category and supplier are required HERE, on creation, and deliberately NOT in
     * ProductService / the database.
     *
     * Required, because a product without them is quietly degraded rather than merely
     * incomplete: no category means the CATEGORY level of the coefficient cascade and the
     * customer×category / group×category discount levels can never apply to it, and no supplier
     * means purchasing and auto-PO have nothing to fire at. Both failures are silent — the
     * product sells at the wrong price instead of erroring.
     *
     * Form-only, because ProductService.create is shared with paths that legitimately have
     * neither: the file import (a vendor file may carry no category column, and the file-level
     * default may be blank) and TecDoc auto-enrich, which CREATES the product and only then
     * discovers its category. Enforcing this in the service would break both. If this asymmetry
     * later looks like an oversight to be "tidied up" — it is not; read this comment first.
     */
    if (isNew && !form.categoryId) {
      return t('productAdmin.validation.categoryRequired');
    }
    if (isNew && !form.mainSupplierId) {
      return t('productAdmin.validation.supplierRequired');
    }

    const purchase = Number(form.purchasePriceHT || 0);
    if (purchase < 0) return t('productAdmin.validation.negativePurchase');

    const override =
      form.sellingPriceOverride.trim() === '' ? null : Number(form.sellingPriceOverride);
    const coef = form.coefficient.trim() === '' ? null : Number(form.coefficient);

    if (override != null) {
      if (override <= purchase) {
        return t('productAdmin.validation.overrideTooLow', {
          override: formatEUR(override),
          purchase: formatEUR(purchase),
        });
      }
      return null;
    }
    // Coefficient path — selling price = purchase × coefficient must exceed purchase.
    if (purchase <= 0) {
      return t('productAdmin.validation.noPrice');
    }
    if (coef != null && coef <= 1) {
      return t('productAdmin.validation.coefficientTooLow');
    }
    return null;
  };

  // Actually create the product (called directly, or after the operator confirms past duplicates).
  const performCreate = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.post<Product>(
        '/products',
        { sku: form.sku.trim(), ...buildPayload() },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      // The sheet only carried the scalar fields. The rest of the article — OE/cross references,
      // images, specifications and the vehicle fitment links — is written by the enricher, which
      // needed a product id. overwrite:false so anything the operator corrected on the sheet wins.
      if (pendingArt) {
        try {
          await apiClient.post(
            '/admin/tecdoc/enrich',
            { productId: data.id, artId: pendingArt.snapshot.artId, overwrite: false },
            { headers: { 'X-Command-Id': crypto.randomUUID() } }
          );
        } catch {
          onSaved(t('productAdmin.saved.createdNoTecdoc', { name: form.name.trim() }));
          return;
        }
      }
      onSaved(t('productAdmin.saved.created', { name: form.name.trim() }));
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
      setDupConfirm(null);
    }
  };

  const save = async () => {
    const err = validate();
    if (err) {
      showValidation(err);
      return;
    }
    if (isNew || !current) {
      // Pre-save verification — flag products that already use this SKU / EAN / manufacturer ref
      // before creating. Best-effort: if the check itself fails, fall through to create (the
      // backend still 409s on a true SKU collision).
      setSaving(true);
      try {
        const { data } = await apiClient.post<{
          duplicates: DuplicateHit[];
          enrich: ArticleMatch[];
        }>(
          '/products/validate',
          {
            sku: form.sku.trim() || undefined,
            ean: textOrNull(form.ean) ?? undefined,
            manufacturerRef: textOrNull(form.manufacturerRef) ?? undefined,
            reference: (textOrNull(form.manufacturerRef) ?? form.sku.trim()) || undefined,
          },
          // /products sits behind the idempotency guard — every POST needs an X-Command-Id.
          { headers: { 'X-Command-Id': crypto.randomUUID() } }
        );
        if (data.duplicates.length > 0) {
          setDupConfirm(data);
          setSaving(false);
          return; // wait for explicit confirmation
        }
      } catch {
        // verification unavailable — proceed to create
      }
      await performCreate();
      return;
    }
    setSaving(true);
    try {
      await apiClient.patch(
        `/products/${current.id}`,
        { expectedVersion: current.version, ...buildPayload() },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      onSaved(t('productAdmin.saved.updated', { name: form.name.trim() }));
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <aside className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border bg-background shadow-xl">
        <header className="flex items-center justify-between border-b px-5 py-3">
          <div>
            <h2 className="text-lg font-semibold">
              {isNew ? t('productAdmin.drawer.newTitle') : t('productAdmin.drawer.editTitle')}
            </h2>
            {current && (
              <p className="font-mono text-xs text-muted-foreground">
                {current.sku} · v{current.version}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            aria-label={t('productAdmin.drawer.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <p className="text-xs text-muted-foreground">{t('productAdmin.drawer.intro')}</p>

          <div className="grid grid-cols-1 gap-x-8 gap-y-3 lg:grid-cols-3">
            {/* ---------------- Column 1 ---------------- */}
            <div className="space-y-3">
              <Field label={t('productAdmin.fields.sku')} required>
                <input
                  ref={firstField}
                  value={form.sku}
                  onChange={(e) => set('sku', e.target.value)}
                  disabled={lockSheet}
                  placeholder={t('productAdmin.fields.sku')}
                  className={fieldCls + ' font-mono disabled:opacity-60'}
                />
                {skuRenamed && (
                  <p className="mt-1 text-[11px] leading-snug text-amber-600">
                    {t('productAdmin.drawer.renaming', { sku: current?.sku })}
                  </p>
                )}
              </Field>
              <Field label={t('productAdmin.fields.description')} required>
                <input
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  disabled={lockSheet}
                  placeholder={t('productAdmin.fields.description')}
                  className={fieldCls + ' disabled:opacity-60'}
                />
              </Field>
              <Field label={t('productAdmin.fields.grossPrice')}>
                <input
                  type="number"
                  step="0.01"
                  value={form.sellingPriceOverride}
                  onChange={(e) => set('sellingPriceOverride', e.target.value)}
                  placeholder={t('productAdmin.fields.grossPricePh')}
                  disabled={lockSheet}
                  className={fieldCls + ' tabular-nums disabled:opacity-60'}
                />
              </Field>
              <Field
                label={
                  canWriteCost
                    ? t('productAdmin.fields.purchasePrice')
                    : t('productAdmin.fields.purchasePriceReadOnly')
                }
              >
                <input
                  type="number"
                  step="0.01"
                  value={form.purchasePriceHT}
                  onChange={(e) => set('purchasePriceHT', e.target.value)}
                  disabled={!canWriteCost}
                  title={canWriteCost ? undefined : t('productAdmin.fields.purchasePriceLocked')}
                  placeholder={t('productAdmin.fields.purchasePricePh')}
                  className={
                    fieldCls +
                    ' tabular-nums' +
                    (canWriteCost ? '' : ' opacity-60 cursor-not-allowed')
                  }
                />
              </Field>
              <DisabledField
                label={t('productAdmin.fields.currentStock')}
                placeholder={t('productAdmin.fields.currentStockPh')}
              />
              <Field label={t('productAdmin.fields.minStock')}>
                <input
                  type="number"
                  value={form.lowStockThreshold}
                  onChange={(e) => set('lowStockThreshold', e.target.value)}
                  disabled={lockSheet}
                  placeholder={t('productAdmin.fields.minStock')}
                  className={fieldCls + ' tabular-nums disabled:opacity-60'}
                />
              </Field>
              <FieldInput
                label={t('productAdmin.fields.maxStock')}
                type="number"
                value={form.maxStockThreshold}
                onChange={(v) => set('maxStockThreshold', v)}
                disabled={lockSheet}
              />
              {/* Where the part is: set by scanning the part then the shelf on the Shelving
                  screen (migration 144), never typed here. Main location first. */}
              <Field label={t('productAdmin.fields.locations')}>
                <div
                  className="flex min-h-9 items-center"
                  title={t('productAdmin.fields.locationsTitle')}
                >
                  {current ? (
                    <ShelfLocations productId={current.id} locations={current.locations ?? []} />
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {t('productAdmin.fields.locationsEmpty')}
                    </span>
                  )}
                </div>
              </Field>
              <FieldSelect
                label={
                  isNew
                    ? t('productAdmin.fields.supplier') + ' *'
                    : t('productAdmin.fields.supplier')
                }
                value={form.mainSupplierId}
                onChange={(v) => set('mainSupplierId', v)}
                disabled={lockSheet}
                emptyLabel={t('productAdmin.none')}
                options={suppliers.map((s) => ({ value: s.id, label: `${s.code} · ${s.name}` }))}
              />
              <FieldInput
                label={t('productAdmin.fields.subGroupCode')}
                value={form.subGroupCode}
                onChange={(v) => set('subGroupCode', v)}
                disabled={lockSheet}
              />
              <Field label={t('productAdmin.fields.oem')}>
                <input
                  value={form.manufacturerRef}
                  onChange={(e) => set('manufacturerRef', e.target.value)}
                  disabled={lockSheet}
                  placeholder={t('productAdmin.fields.oem')}
                  className={fieldCls + ' font-mono disabled:opacity-60'}
                />
              </Field>
              <Field label={t('productAdmin.fields.brand')}>
                <input
                  value={form.brand}
                  onChange={(e) => set('brand', e.target.value)}
                  disabled={lockSheet}
                  placeholder={t('productAdmin.fields.brand')}
                  className={fieldCls + ' disabled:opacity-60'}
                />
              </Field>
              <FieldInput
                label={t('productAdmin.fields.productGroupId')}
                value={form.productGroupId}
                onChange={(v) => set('productGroupId', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.attributeId')}
                value={form.attributeId}
                onChange={(v) => set('attributeId', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.fileId')}
                value={form.fileId}
                onChange={(v) => set('fileId', v)}
                disabled={lockSheet}
              />
            </div>

            {/* ---------------- Column 2 ---------------- */}
            <div className="space-y-3">
              <FieldSelect
                label={t('productAdmin.fields.referenceType', { n: 1 })}
                value={form.referenceType1}
                onChange={(v) => set('referenceType1', v)}
                disabled={lockSheet}
                options={REFERENCE_TYPES.map((rt) => ({ value: rt, label: rt }))}
              />
              <FieldInput
                label={t('productAdmin.fields.reference', { n: 2 })}
                value={form.reference2}
                onChange={(v) => set('reference2', v)}
                disabled={lockSheet}
                mono
              />
              <FieldSelect
                label={t('productAdmin.fields.referenceType', { n: 2 })}
                value={form.referenceType2}
                onChange={(v) => set('referenceType2', v)}
                disabled={lockSheet}
                options={REFERENCE_TYPES.map((rt) => ({ value: rt, label: rt }))}
              />
              <FieldInput
                label={t('productAdmin.fields.reference', { n: 3 })}
                value={form.reference3}
                onChange={(v) => set('reference3', v)}
                disabled={lockSheet}
                mono
              />
              <FieldSelect
                label={t('productAdmin.fields.referenceType', { n: 3 })}
                value={form.referenceType3}
                onChange={(v) => set('referenceType3', v)}
                disabled={lockSheet}
                options={REFERENCE_TYPES.map((rt) => ({ value: rt, label: rt }))}
              />
              <FieldInput
                label={t('productAdmin.fields.reference', { n: 4 })}
                value={form.reference4}
                onChange={(v) => set('reference4', v)}
                disabled={lockSheet}
                mono
              />
              <FieldSelect
                label={t('productAdmin.fields.referenceType', { n: 4 })}
                value={form.referenceType4}
                onChange={(v) => set('referenceType4', v)}
                disabled={lockSheet}
                options={REFERENCE_TYPES.map((rt) => ({ value: rt, label: rt }))}
              />
              <FieldInput
                label={t('productAdmin.fields.productKeys')}
                value={form.productKeys}
                onChange={(v) => set('productKeys', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.heightMm')}
                type="number"
                step="0.01"
                value={form.heightMm}
                onChange={(v) => set('heightMm', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.lengthMm')}
                type="number"
                step="0.01"
                value={form.lengthMm}
                onChange={(v) => set('lengthMm', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.widthMm')}
                type="number"
                step="0.01"
                value={form.widthMm}
                onChange={(v) => set('widthMm', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.inch')}
                type="number"
                step="0.01"
                value={form.inch}
                onChange={(v) => set('inch', v)}
                disabled={lockSheet}
              />
              <Field label={t('productAdmin.fields.netWeight')}>
                <input
                  type="number"
                  step="0.001"
                  value={form.weightKg}
                  onChange={(e) => set('weightKg', e.target.value)}
                  disabled={lockSheet}
                  placeholder={t('productAdmin.fields.netWeightPh')}
                  className={fieldCls + ' tabular-nums disabled:opacity-60'}
                />
              </Field>
              <FieldInput
                label={t('productAdmin.fields.packingMaterial')}
                value={form.packingMaterial}
                onChange={(v) => set('packingMaterial', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.labelType')}
                value={form.labelType}
                onChange={(v) => set('labelType', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.photoUrl')}
                value={form.photoUrl}
                onChange={(v) => set('photoUrl', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.popularityCode')}
                value={form.popularityCode}
                onChange={(v) => set('popularityCode', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.statusCode')}
                value={form.statusCode}
                onChange={(v) => set('statusCode', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.returnCode')}
                value={form.returnCode}
                onChange={(v) => set('returnCode', v)}
                disabled={lockSheet}
              />
            </div>

            {/* ---------------- Column 3 ---------------- */}
            <div className="space-y-3">
              <FieldSelect
                label={t('productAdmin.fields.currency', { n: 1 })}
                value={form.currency1}
                onChange={(v) => set('currency1', v)}
                disabled={lockSheet}
                options={CURRENCIES.map((c) => ({ value: c, label: c }))}
              />
              <FieldSelect
                label={t('productAdmin.fields.currency', { n: 2 })}
                value={form.currency2}
                onChange={(v) => set('currency2', v)}
                disabled={lockSheet}
                options={CURRENCIES.map((c) => ({ value: c, label: c }))}
              />
              <FieldInput
                label={t('productAdmin.fields.groupCodeDiscount2')}
                value={form.groupCodeDiscount2}
                onChange={(v) => set('groupCodeDiscount2', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.groupCodeIn', { n: 1 })}
                value={form.groupCodeIn1}
                onChange={(v) => set('groupCodeIn1', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.groupCodeIn', { n: 2 })}
                value={form.groupCodeIn2}
                onChange={(v) => set('groupCodeIn2', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.groupCodeEx', { n: 1 })}
                value={form.groupCodeEx1}
                onChange={(v) => set('groupCodeEx1', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.groupCodeEx', { n: 2 })}
                value={form.groupCodeEx2}
                onChange={(v) => set('groupCodeEx2', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.minOrderQty')}
                type="number"
                value={form.minOrderQty}
                onChange={(v) => set('minOrderQty', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.conditioningQty')}
                type="number"
                value={form.conditioningQty}
                onChange={(v) => set('conditioningQty', v)}
                disabled={lockSheet}
              />
              <Field label={t('productAdmin.fields.packagingType')}>
                <select
                  value={form.packagingUnit}
                  onChange={(e) => set('packagingUnit', e.target.value)}
                  disabled={lockSheet}
                  className={fieldCls + ' disabled:opacity-60'}
                >
                  {PACKAGING_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {t(`productAdmin.packaging.${u}`)}
                    </option>
                  ))}
                </select>
              </Field>
              {/* Migration 134: the VAT rate never sits on the product — it is the standard rate from
                  settings, or 0 % for a customer marked VAT-exempt (intra-EU / export). */}
              <Field label={t('productAdmin.fields.vat')}>
                <input
                  type="text"
                  value={t('productAdmin.fields.vatValue')}
                  readOnly
                  disabled
                  title={t('productAdmin.fields.vatTitle')}
                  className={fieldCls + ' disabled:opacity-60'}
                />
              </Field>
              <FieldInput
                label={t('productAdmin.fields.disposalFee')}
                type="number"
                step="0.01"
                value={form.disposalFee}
                onChange={(v) => set('disposalFee', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.deposit')}
                type="number"
                step="0.01"
                value={form.depositAmountHt}
                onChange={(v) => set('depositAmountHt', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.environmentalSurcharge')}
                type="number"
                step="0.01"
                value={form.environmentalSurcharge}
                onChange={(v) => set('environmentalSurcharge', v)}
                disabled={lockSheet}
              />
              <FieldInput
                label={t('productAdmin.fields.grossWeight')}
                type="number"
                step="0.001"
                value={form.grossWeightKg}
                onChange={(v) => set('grossWeightKg', v)}
                disabled={lockSheet}
              />
            </div>
          </div>

          {canEnrich && (
            <>
              <EnrichPanel
                product={current}
                seedRef={form.manufacturerRef || form.sku}
                seedBrand={form.brand}
                onPreview={applyPreview}
                onEnriched={applyEnriched}
                onError={onError}
              />
              {filledFrom && (
                <p className="text-xs text-primary">
                  {t('productAdmin.drawer.filledFrom', { article: filledFrom })}
                </p>
              )}
            </>
          )}
          {/* The enriched data itself: the saved product's once it exists, the pending article's
              while the sheet is still a draft — so the operator can see what they are about to
              attach, not only the four scalar fields that fit in the inputs. */}
          {current ? (
            <TecDocDataPanel data={current} />
          ) : (
            pendingArt && <TecDocDataPanel data={pendingArt.snapshot} />
          )}

          {/* Catalogue fields kept functional but outside the source sheet's columns. */}
          <Section title={t('productAdmin.drawer.advanced')}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <Field label={t('productAdmin.fields.shortName')}>
                <input
                  value={form.shortName}
                  onChange={(e) => set('shortName', e.target.value)}
                  disabled={lockSheet}
                  className={fieldCls + ' disabled:opacity-60'}
                />
              </Field>
              <Field label={t('productAdmin.fields.ean')}>
                <input
                  value={form.ean}
                  onChange={(e) => set('ean', e.target.value)}
                  disabled={lockSheet}
                  className={fieldCls + ' font-mono disabled:opacity-60'}
                />
              </Field>
              <Field label={t('productAdmin.fields.ean2')}>
                <input
                  value={form.ean2}
                  onChange={(e) => set('ean2', e.target.value)}
                  disabled={lockSheet}
                  placeholder={t('productAdmin.fields.ean2Ph')}
                  className={fieldCls + ' font-mono disabled:opacity-60'}
                />
              </Field>
              <Field
                label={
                  isNew
                    ? t('productAdmin.fields.category') + ' *'
                    : t('productAdmin.fields.category')
                }
              >
                <div className="flex items-center gap-2">
                  <span className="flex-1 truncate text-sm" title={categoryLabel || undefined}>
                    {categoryLabel || (
                      <span className={isNew ? 'text-destructive' : 'text-muted-foreground'}>
                        {t('productAdmin.fields.unclassified')}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPickCat(true)}
                    disabled={lockSheet}
                    className={
                      buttonVariants({ variant: 'outline', size: 'sm' }) + ' disabled:opacity-60'
                    }
                  >
                    {t('productAdmin.fields.choose')}
                  </button>
                  {form.categoryId && !lockSheet && (
                    <button
                      type="button"
                      onClick={() => {
                        set('categoryId', '');
                        setCategoryLabel('');
                      }}
                      className="text-xs text-muted-foreground hover:text-destructive"
                    >
                      {t('productAdmin.fields.remove')}
                    </button>
                  )}
                </div>
              </Field>
              <Field label={t('productAdmin.fields.origin')}>
                <select
                  value={form.origin}
                  onChange={(e) => set('origin', e.target.value)}
                  disabled={lockSheet}
                  className={fieldCls + ' disabled:opacity-60'}
                >
                  {ORIGINS.map((o) => (
                    <option key={o} value={o}>
                      {t(`productAdmin.origins.${o}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t('productAdmin.fields.coefficient')}>
                <input
                  type="number"
                  step="0.001"
                  value={form.coefficient}
                  onChange={(e) => set('coefficient', e.target.value)}
                  placeholder={t('productAdmin.inherit')}
                  disabled={lockSheet}
                  className={fieldCls + ' tabular-nums disabled:opacity-60'}
                />
                {/* This line is the NO-FAMILY figure — what an anonymous counter sale pays. A
                    customer with a family prices off the grid, which is per-family and shown in
                    its own table below rather than collapsed into one number here. */}
                {resolvedCoef && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('productAdmin.coef.withoutFamily')}{' '}
                    <strong className="tabular-nums">{resolvedCoef.value}</strong>{' '}
                    {t('productAdmin.coef.from')} {resolvedCoef.label}
                    {effectiveSelling != null && (
                      <>
                        {' · '}
                        {t('productAdmin.coef.selling')}{' '}
                        <strong className="tabular-nums">{formatEUR(effectiveSelling)} HT</strong>
                      </>
                    )}
                  </p>
                )}
              </Field>
              <Field label={t('productAdmin.fields.longDescription')}>
                <input
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  disabled={lockSheet}
                  className={fieldCls + ' disabled:opacity-60'}
                />
              </Field>
            </div>

            {/* THE INHERITANCE ROW — this article's coefficient for each customer family, and
                where each one comes from. It sits directly under the Coefficient field because
                that field is the family-agnostic override the table has to be read against: a
                number typed there moves every family that has no override of its own. */}
            <div className="mt-4 border-t pt-3">
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                {t('productAdmin.coef.perFamily')}
              </p>
              <ProductCoefficientInheritance productId={current?.id ?? null} />
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => set('active', e.target.checked)}
                disabled={lockSheet}
                className={checkboxClass}
              />
              {t('productAdmin.fields.activeForSale')}
            </label>
          </Section>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t px-5 py-3">
          <button onClick={onClose} className={buttonVariants({ variant: 'outline', size: 'sm' })}>
            {t('productAdmin.cancel')}
          </button>
          <Button size="sm" onClick={save} disabled={saving}>
            <Save className="h-4 w-4" />
            {saving
              ? t('productAdmin.drawer.saving')
              : isNew
                ? t('productAdmin.drawer.create')
                : t('productAdmin.drawer.saveChanges')}
          </Button>
        </footer>
      </aside>

      {pickCat && (
        <CategoryPicker
          onClose={() => setPickCat(false)}
          onPick={(id, name) => {
            set('categoryId', id ?? '');
            setCategoryLabel(name);
            setPickCat(false);
          }}
        />
      )}

      {dupConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDupConfirm(null)}
            aria-hidden
          />
          <div className="relative w-full max-w-lg rounded-lg bg-background p-5 shadow-xl">
            <h3 className="text-base font-semibold">{t('productAdmin.duplicate.title')}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('productAdmin.duplicate.intro', { count: dupConfirm.duplicates.length })}
            </p>
            <ul className="mt-3 divide-y rounded-md border">
              {dupConfirm.duplicates.map((d) => (
                <li
                  key={`${d.field}-${d.id}`}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                >
                  <span className="min-w-0">
                    <span className="font-mono text-xs">{d.sku}</span>
                    <span className="text-muted-foreground"> · {d.name}</span>
                  </span>
                  <Badge variant="warning">{t(`productAdmin.duplicate.fields.${d.field}`)}</Badge>
                </li>
              ))}
            </ul>
            {dupConfirm.enrich.length > 0 && (
              <p className="mt-3 text-xs text-muted-foreground">
                {t('productAdmin.duplicate.tecdoc', { count: dupConfirm.enrich.length })}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDupConfirm(null)}>
                <X className="h-4 w-4" />
                {t('productAdmin.cancel')}
              </Button>
              <Button size="sm" onClick={() => void performCreate()} disabled={saving}>
                <Check className="h-4 w-4" />
                {saving
                  ? t('productAdmin.duplicate.creating')
                  : t('productAdmin.duplicate.createAnyway')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** The dense product sheet keeps its own handle on the field surface, but the surface itself is
 *  the platform's — not a local copy that drifts out of step with <Input>. */
const fieldCls = inputClass;

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-muted-foreground">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </span>
      {children}
    </label>
  );
}

// Currency options for the product sheet's Currency 1/2 pickers.
const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF'] as const;

/** Wired text/number input bound to a form field. */
function FieldInput({
  label,
  value,
  onChange,
  disabled,
  type,
  step,
  mono,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  type?: 'text' | 'number';
  step?: string;
  mono?: boolean;
  placeholder?: string;
}) {
  return (
    <Field label={label}>
      <input
        type={type ?? 'text'}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder ?? label}
        className={
          fieldCls +
          (mono ? ' font-mono' : '') +
          (type === 'number' ? ' tabular-nums' : '') +
          ' disabled:opacity-60'
        }
      />
    </Field>
  );
}

/** Wired select bound to a form field; the first option is an empty/"none" choice. */
function FieldSelect({
  label,
  value,
  onChange,
  disabled,
  emptyLabel,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  emptyLabel?: string;
  options: { value: string; label: string }[];
}) {
  const { t } = useTranslation();
  return (
    <Field label={label}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={fieldCls + ' disabled:opacity-60'}
      >
        <option value="">{emptyLabel ?? t('productAdmin.none')}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

/** Placeholder for a field the backend doesn't persist yet — shown disabled for layout fidelity. */
function DisabledField({
  label,
  placeholder,
  options,
}: {
  label: string;
  placeholder?: string;
  options?: string[];
}) {
  return (
    <Field label={label}>
      {options ? (
        <select disabled className={fieldCls + ' opacity-60'}>
          <option>{placeholder ?? options[0]}</option>
          {options.map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      ) : (
        <input disabled placeholder={placeholder ?? label} className={fieldCls + ' opacity-60'} />
      )}
    </Field>
  );
}

// ---------------------------------------------------------------------------
// TecDoc enrichment (search → pick → enrich) + read-only enriched data
// ---------------------------------------------------------------------------

/** Search TecDoc by reference, pick a matching article, and enrich the product from it. */
function EnrichPanel({
  product,
  seedRef,
  seedBrand,
  onPreview,
  onEnriched,
  onError,
}: {
  product: Product | null;
  seedRef: string;
  seedBrand: string;
  /** New product: fill the sheet from a read-only preview — nothing is created yet. */
  onPreview: (preview: ArticlePreview, overwrite: boolean) => void;
  /** Existing product: the article has just been written to it; reload + reflect it. */
  onEnriched: (productId: string, snapshot: ArticleSnapshot, overwrite: boolean) => Promise<void>;
  onError: (err: unknown) => void;
}) {
  const { t } = useTranslation();
  const [ref, setRef] = useState(seedRef);
  const [brand, setBrand] = useState(seedBrand);
  const [overwrite, setOverwrite] = useState(false);
  const [searching, setSearching] = useState(false);
  const [enrichingId, setEnrichingId] = useState<number | null>(null);
  const [candidates, setCandidates] = useState<ArticleMatch[] | null>(null);

  const search = async () => {
    if (!ref.trim()) return;
    setSearching(true);
    setCandidates(null);
    try {
      const p = new URLSearchParams({ number: ref.trim() });
      if (brand.trim()) p.set('brand', brand.trim());
      const { data } = await apiClient.get<{ data: ArticleMatch[] }>(
        `/admin/tecdoc/article-search?${p.toString()}`
      );
      setCandidates(data.data);
    } catch (err) {
      onError(err);
    } finally {
      setSearching(false);
    }
  };

  const enrich = async (artId: number) => {
    setEnrichingId(artId);
    try {
      if (product) {
        const { data } = await apiClient.post<{ data: { snapshot: ArticleSnapshot } }>(
          '/admin/tecdoc/enrich',
          { productId: product.id, artId, overwrite },
          { headers: { 'X-Command-Id': crypto.randomUUID() } }
        );
        await onEnriched(product.id, data.data.snapshot, overwrite);
      } else {
        // No product row to write to yet — fetch the same snapshot read-only and fill the sheet.
        const { data } = await apiClient.get<{ data: ArticlePreview }>(
          `/admin/tecdoc/article/${artId}`
        );
        onPreview(data.data, overwrite);
      }
      setCandidates(null);
    } catch (err) {
      onError(err);
    } finally {
      setEnrichingId(null);
    }
  };

  return (
    <section className="space-y-3 rounded-lg border bg-muted/20 p-3">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('productAdmin.enrich.title')}
        </h3>
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-2">
        <div className="grid grid-cols-2 gap-2">
          <input
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void search()}
            placeholder={t('productAdmin.enrich.refPlaceholder')}
            className={fieldCls + ' font-mono'}
          />
          <input
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void search()}
            placeholder={t('productAdmin.enrich.brandPlaceholder')}
            className={fieldCls}
          />
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void search()}
          disabled={searching || !ref.trim()}
        >
          <Search className="h-4 w-4" />{' '}
          {searching ? t('productAdmin.enrich.searching') : t('productAdmin.enrich.search')}
        </Button>
      </div>

      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={overwrite}
          onChange={(e) => setOverwrite(e.target.checked)}
          className={checkboxClass}
        />
        {t('productAdmin.enrich.overwrite')}
      </label>

      {!product && (
        <p className="text-xs text-muted-foreground">{t('productAdmin.enrich.newHint')}</p>
      )}

      {candidates && candidates.length === 0 && (
        <p className="text-xs text-muted-foreground">{t('productAdmin.enrich.noResult')}</p>
      )}
      {candidates && candidates.length > 0 && (
        <ul className="divide-y rounded-md border bg-background">
          {candidates.map((c) => (
            <li key={c.artId} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className="truncate font-mono text-sm">{c.articleNr}</div>
                <div className="text-xs text-muted-foreground">
                  {c.brand} · {t('productAdmin.enrich.matchedOn', { type: c.matchedType })}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => void enrich(c.artId)}
                disabled={enrichingId !== null}
              >
                <Sparkles className="h-4 w-4" />
                {enrichingId === c.artId
                  ? t('productAdmin.enrich.enriching')
                  : t('productAdmin.enrich.enrich')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** TecDoc media can be a raster image or an HTML document (e.g. an interactive diagram). */
const isHtmlAsset = (url: string) => /\.x?html?(\?|#|$)/i.test(url);

/** An absolute manufacturer link stays as-is; a "<supplierId>/<file>" key is ours to serve. */
const isAbsoluteAsset = (url: string) => /^https?:\/\//i.test(url);
const articleMediaSrc = (url: string) =>
  isAbsoluteAsset(url) ? tecdocAssetUrl(url) : `/catalog/media/article/${url}`;

// TecDoc media is served over http; the host also supports https, so upgrade the scheme.
// The HTTPS app would otherwise block these images/frames as mixed content.
const tecdocAssetUrl = (url: string) => url.replace(/^http:\/\//i, 'https://');

/**
 * Read-only display of the fields TecDoc enrichment fills (specs, refs, images). Takes the data
 * rather than a Product, so it renders a pending article's snapshot on a not-yet-created product
 * exactly as it renders a saved one's.
 */
function TecDocDataPanel({
  data,
}: {
  data: {
    imageUrls: string[];
    oemReferences: string[];
    crossReferences: string[];
    specifications: Spec[];
  };
}) {
  const { t } = useTranslation();
  const { imageUrls, oemReferences, crossReferences, specifications } = data;
  if (
    !imageUrls.length &&
    !oemReferences.length &&
    !crossReferences.length &&
    !specifications.length
  ) {
    return null;
  }
  // TecDoc returns a mix: most assets are images, some are HTML documents. Render each by type
  // so HTML files aren't forced through <img> (which would show a broken-image icon).
  const images = imageUrls.filter((url) => !isHtmlAsset(url));
  const htmlDocs = imageUrls.filter(isHtmlAsset);
  return (
    <Section title={t('productAdmin.tecdocData.title')}>
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.slice(0, 6).map((url) =>
            // Our own media route is session-gated, so those go through ArticleImage (which
            // fetches them with the bearer token); an absolute link also gets a full-size anchor.
            isAbsoluteAsset(url) ? (
              <a
                key={url}
                href={tecdocAssetUrl(url)}
                target="_blank"
                rel="noreferrer"
                title={t('productAdmin.tecdocData.openFullSize')}
              >
                <img
                  src={tecdocAssetUrl(url)}
                  alt=""
                  loading="lazy"
                  className="h-16 w-16 rounded-md border bg-white object-contain"
                />
              </a>
            ) : (
              <ArticleImage
                key={url}
                src={articleMediaSrc(url)}
                alt=""
                size={64}
                className="rounded-md border bg-white object-contain"
              />
            )
          )}
        </div>
      )}
      {htmlDocs.length > 0 && (
        <div className="space-y-2">
          {htmlDocs.map((url) => (
            <div key={url} className="overflow-hidden rounded-md border bg-white">
              <iframe
                src={tecdocAssetUrl(url)}
                title={t('productAdmin.tecdocData.document')}
                loading="lazy"
                // TecDoc HTML assets are script-driven image viewers (e.g. Object2VR); allow
                // scripts so they render, but withhold allow-top-navigation so the framed
                // third-party page can't redirect the app.
                sandbox="allow-scripts allow-same-origin allow-popups"
                className="h-64 w-full bg-white"
              />
              <a
                href={tecdocAssetUrl(url)}
                target="_blank"
                rel="noreferrer"
                className="block border-t bg-background px-2 py-1 text-right text-xs text-primary hover:underline"
              >
                {t('productAdmin.tecdocData.openInNewTab')}
              </a>
            </div>
          ))}
        </div>
      )}
      {specifications.length > 0 && (
        <div className="overflow-hidden rounded-md border">
          <table className="w-full text-sm">
            <tbody className="[&_tr:last-child]:border-0">
              {specifications.map((s, i) => (
                <tr key={`${s.label}-${i}`} className="border-t">
                  <td className="px-3 py-1.5 text-muted-foreground">{s.label}</td>
                  <td className="px-3 py-1.5 text-right font-medium tabular-nums">
                    {s.value}
                    {s.unit ? ` ${s.unit}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {oemReferences.length > 0 && (
        <RefChips label={t('productAdmin.tecdocData.oemReferences')} items={oemReferences} />
      )}
      {crossReferences.length > 0 && (
        <RefChips label={t('productAdmin.tecdocData.crossReferences')} items={crossReferences} />
      )}
    </Section>
  );
}

function RefChips({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="flex flex-wrap gap-1">
        {items.map((r) => (
          <span key={r} className="rounded border bg-background px-1.5 py-0.5 font-mono text-xs">
            {r}
          </span>
        ))}
      </div>
    </div>
  );
}
